import { useDispatch } from "react-redux";
import { applyUserPreferences } from "../../app/slices/GlobalSlice";
import {
  setAuthenticationSuccess,
  setIsAuthenticated,
} from "../../app/slices/AuthenticationSlice";
import { AuthenticationManager } from "@playit/shared-authentication";
import { setOrganizations, setUserInfo } from "../../app/slices/UserSlice";
import { UserService } from "../../services/UserService";
import { useState } from "react";
import {
  DEFAULT_USER_SETTINGS,
  UserSettingsService,
} from "../../services/UserSettingsService";
import { setUserSettingsLoaded } from "../../app/slices/UserSettingsSlice";
import { setUserSettingsLoadFailed } from "../../app/slices/UserSettingsSlice";
import { setOnboardingHydrated } from "../../app/slices/OnboardingSlice";

export const useLogin = () => {
  const [isOrganizationsLoading, setIsOrganizationsLoading] = useState(false);
  const dispatch = useDispatch();

  const onLoginSuccessful = async (
    email: string,
    jwt: string,
    refreshToken: string,
    userId: string,
    organizationIds: string[]
  ) => {
    setIsOrganizationsLoading(true);
    // auth slice
    dispatch(
      setAuthenticationSuccess({
        email: email,
        jwt: jwt,
        userID: userId,
        organizationIDs: organizationIds,
        refreshToken: refreshToken,
      })
    );

    // set user info
    const userInfo = AuthenticationManager.GetUserDetails();
    if (userInfo) dispatch(setUserInfo(userInfo));

    const userSettingsResult = await UserSettingsService.GetUserSettings(jwt);
    if (userSettingsResult.status === "loaded") {
      const userSettings = userSettingsResult.settings;
      dispatch(setUserSettingsLoaded(userSettings));
      const preferences = userSettings ?? DEFAULT_USER_SETTINGS;
      dispatch(
        applyUserPreferences({
          darkMode: preferences.darkMode,
          autoSave: preferences.autoSave,
        })
      );
      dispatch(setOnboardingHydrated(userSettings?.onboarding ?? null));
    } else {
      // Settings failures must not look like a new user. Keep onboarding
      // unhydrated so no default progress is shown or written over the server.
      dispatch(setUserSettingsLoadFailed());
      dispatch(
        applyUserPreferences({
          darkMode: DEFAULT_USER_SETTINGS.darkMode,
          autoSave: DEFAULT_USER_SETTINGS.autoSave,
        })
      );
    }

    // set organizations
    const orgs = await UserService.GetOrganizations(jwt, userId);
    if (orgs !== null) dispatch(setOrganizations(orgs));

    dispatch(setIsAuthenticated(true));

    setIsOrganizationsLoading(false);
  };

  return { onLoginSuccessful, isOrganizationsLoading } as const;
};
